import React, { useEffect } from "react";
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Paper, 
  Avatar, 
  Alert,
  TextField 
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useForgotPasswordMutation } from "../../api/authApi";
import { useFormSubmission } from "../../hooks/useFormSubmission";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required")
    .max(256, "Email cannot exceed 256 characters"),
});

interface FormValues {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [forgotPassword] = useForgotPasswordMutation();

  const {
    isSubmitting,
    submitError,
    successMessage,
    handleSubmit,
    clearMessages,
  } = useFormSubmission({
    onSuccess: () => {
      // Success message will be shown in the form
    },
    successMessage: "If the email exists, a password reset link has been sent to your inbox.",
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        await forgotPassword({ email: values.email }).unwrap();
      });
    },
  });

  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            component="button"
            onClick={() => navigate("/login")}
            sx={{
              m: 1,
              bgcolor: "primary.main",
              width: 40,
              height: 40,
              cursor: "pointer",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            <ArrowBackIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mt: 1 }}>
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
              {submitError}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mt: 2, mb: 2, width: "100%" }}>
              {successMessage}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ mt: 3, width: "100%" }}
          >
            <TextField
              margin="normal"
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </Button>

            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Remember your password?{" "}
                <Button
                  variant="text"
                  onClick={() => navigate("/login")}
                  sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                >
                  Sign in
                </Button>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
